import type { QuestionType, QuestionValue } from '@lib/types/survey';

export const SKIPPED_VALUE = '__SKIPPED__';
export const ANY_KEY_VALUE = '__ANY_KEY__';

// Add interface for question type properties
interface QuestionTypeProperties {
  updatesInternally: boolean;
  alwaysShowSubmit: boolean;
}

interface ProcessValueOptions {
  type: QuestionType;
  currentValue: QuestionValue;
  newValue: QuestionValue;
  isMultiple?: boolean;
}

interface NavigationOptions {
  type: QuestionType;
  isMultiple?: boolean;
  wasSkipped?: boolean;
  newValue: QuestionValue;
}

// Track touched state for questions
const touchedQuestions = new Set<string>();

export const questionRules = {
  /**
   * Determines if a question type is single selection
   */
  isSingleSelection(type: QuestionType): boolean {
    return ['likert', 'emojiLikert', 'starRating', 'nps', 'thumbs', 'select'].includes(type);
  },

  /**
   * Check if a value is valid (not skipped, not altResponse, and not null)
   */
  isValidValue(value: QuestionValue): boolean {
    return value !== SKIPPED_VALUE && value !== 'altResponse' && value !== null;
  },

  /**
   * Check if there is a valid selection
   */
  hasValidSelection(value: QuestionValue): boolean {
    if (Array.isArray(value)) {
      return value.length > 0 && value.every(v => this.isValidValue(v));
    }
    return this.isValidValue(value);
  },

  /**
   * Mark a question as touched when first interacted with
   */
  markTouched(questionId: string): void {
    touchedQuestions.add(questionId);
  },

  /**
   * Check if a question has been touched
   */
  isTouched(questionId: string): boolean {
    return touchedQuestions.has(questionId);
  },

  /**
   * Reset touched state for a question
   */
  resetTouched(questionId: string): void {
    touchedQuestions.delete(questionId);
  },

  /**
   * Process and validate a new value based on question type and current state
   */
  processValue({ type, currentValue, newValue, isMultiple }: ProcessValueOptions): QuestionValue {
    // Handle skipped state
    const wasSkipped = Array.isArray(currentValue) 
      ? currentValue.includes(SKIPPED_VALUE)
      : currentValue === SKIPPED_VALUE;

    // For multiple selection
    if (isMultiple && Array.isArray(newValue)) {
      // Remove any skipped values
      const cleanValue = newValue.filter(v => v !== SKIPPED_VALUE);
      
      // For multiple selection, if we're coming from a skipped state,
      // we want to start fresh with just the new selection
      if (wasSkipped) {
        return cleanValue;
      }
      
      // Otherwise, keep existing selections
      return cleanValue.length > 0 ? cleanValue : null;
    }

    // For single selection
    if (this.isSingleSelection(type) || !isMultiple) {
      // If selecting the same value, keep it (prevent deselection)
      if (newValue === currentValue && !wasSkipped) {
        return currentValue;
      }
      // For new selections, always use the new value
      return newValue;
    }

    // Handle skipped value
    if (newValue === SKIPPED_VALUE) {
      return SKIPPED_VALUE;
    }

    return newValue;
  },

  /**
   * Determines if navigation should occur based on question type and state
   */
  shouldNavigate({ type, isMultiple, wasSkipped, newValue }: NavigationOptions): boolean {
    // Multiple selection questions OR text questions OR audio questions should never auto-navigate
    if (isMultiple === true || type === 'text' || type === 'audio') {
      return false;
    }

    // For single selection questions (either by type or multiple: false)
    // Always navigate on any value, including re-selection of the same value
    // Unless we're coming from a skipped state
    if (wasSkipped) {
      return newValue !== null;
    }
    
    return true;
  },

  /**
   * Determines if submit button should be shown
   */
  shouldShowSubmit(type: QuestionType, isMultiple?: boolean, requireConfirmation?: boolean, value?: QuestionValue, questionId?: string): boolean {
    // Show submit button if:
    // 1. requireConfirmation is true
    // 2. OR it's a multiple selection question (multitext or boxes)
    // 3. OR it's a text input question
    // 4. OR it's a date input question
    // 5. OR it's a date range input question
    // 6. OR it's an audio question
    const shouldShow = requireConfirmation ||
      (type === 'multitext' && isMultiple === true) ||
      (type === 'boxes' && isMultiple === true) ||
      (type === 'text') ||
      (type === 'date') ||
      (type === 'dateRange') ||
      (type === 'audio');

    // If the question has been touched and it's a multiple selection question or text or date,
    // always show the submit button regardless of current value
    if (shouldShow && questionId && this.isTouched(questionId)) {
      return true;
    }

    // Otherwise, check if there's a valid selection
    return shouldShow && (value ? this.hasValidSelection(value) : false);
  },

  /**
   * Process a value for storage
   */
  processStorageValue(value: QuestionValue): QuestionValue {
    if (Array.isArray(value)) {
      const cleanValue = value.filter(v => v !== SKIPPED_VALUE);
      return cleanValue.length > 0 ? cleanValue : null;
    }
    return value === SKIPPED_VALUE ? null : value;
  },

  /**
   * Get properties for a specific question type
   * This centralizes all type-specific behaviors in one place
   */
  getTypeProperties(type: QuestionType): QuestionTypeProperties {
    switch (type) {
      case 'text':
        return {
          updatesInternally: true,
          alwaysShowSubmit: true
        };
      case 'date':
        return {
          updatesInternally: true,
          alwaysShowSubmit: false
        };
      case 'dateRange':
        return {
          updatesInternally: true,
          alwaysShowSubmit: false
        };
      case 'audio':
        return {
          updatesInternally: true,
          alwaysShowSubmit: true
        };
      case 'geolocation':
        return {
          updatesInternally: true,
          alwaysShowSubmit: true
        };
      // Add cases for other question types as needed
      default:
        return {
          updatesInternally: false,
          alwaysShowSubmit: false
        };
    }
  },

  /**
   * Check if a value is valid based on question type
   */
  hasValidValue(type: QuestionType, value: QuestionValue): boolean {
    if (type === 'text') {
      return !!value && typeof value === 'string' && value.trim() !== '';
    }
    if (type === 'date') {
      return !!value && typeof value === 'string' && value.trim() !== '';
    }
    if (type === 'dateRange') {
      return !!value && typeof value === 'object' && value !== null &&
             'from' in value && !!value.from && typeof value.from === 'string';
    }
    if (type === 'audio') {
      return !!value && typeof value === 'object' && (value as any).kind === 'audio';
    }
    if (type === 'geolocation') {
      return !!value && typeof value === 'object' && (value as any).kind === 'geolocation';
    }
    return this.hasValidSelection(value);
  },
}; 